export const AGENT_CHATS_STORAGE_KEY = 'unity-bm-lk.agent-chats';
export const AGENT_CHAT_WIDTH_STORAGE_KEY = 'unity-bm-lk.agent-chat-width';
export const MD_ATTACHMENT_MAX_BYTES = 200 * 1024;
export const TAB_TITLE_MAX_CHARS = 28;
export const AGENT_CHAT_WIDTH_DEFAULT = 380;
export const AGENT_CHAT_WIDTH_MIN = 280;
export const AGENT_CHAT_WIDTH_MAX = 720;
/** From this chat width the left nav collapses to an icon rail. */
export const AGENT_CHAT_SIDEBAR_COLLAPSE_WIDTH = 520;
export function shouldCollapseSidebarForChatWidth(width) {
    return clampChatWidth(width) >= AGENT_CHAT_SIDEBAR_COLLAPSE_WIDTH;
}
export const STUB_AGENT_REPLY = 'Агент ещё не подключён. Сообщение осталось в этом чате.';
export const PENDING_AGENT_REPLY = '';
export const AGENT_CHAT_ERROR_FALLBACK = 'Не удалось получить ответ агента. Попробуйте ещё раз.';
/** Drop offline-stub transcripts left from before the Cursor bridge. */
export function scrubStubChatStore(store) {
    const hasStub = store.tabs.some((tab) => tab.messages.some((message) => message.role === 'assistant' && message.content === STUB_AGENT_REPLY));
    if (!hasStub)
        return store;
    return createInitialStore();
}
let idCounter = 0;
export function createId(prefix) {
    idCounter += 1;
    return `${prefix}-${Date.now()}-${idCounter}`;
}
export function createEmptyTab(now = new Date()) {
    return {
        id: createId('tab'),
        title: 'Новый чат',
        messages: [],
        createdAt: now.toISOString(),
    };
}
export function createInitialStore(now = new Date()) {
    const tab = createEmptyTab(now);
    return {
        tabs: [tab],
        activeTabId: tab.id,
    };
}
export function titleFromUserMessage(content) {
    const trimmed = content
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`[^`]+`/g, ' ')
        .replace(/[*_~#>[\]()!-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!trimmed)
        return 'Новый чат';
    if (trimmed.length <= TAB_TITLE_MAX_CHARS)
        return trimmed;
    return `${trimmed.slice(0, TAB_TITLE_MAX_CHARS - 1)}…`;
}
export function isMarkdownFileName(fileName) {
    const lower = fileName.trim().toLowerCase();
    return lower.endsWith('.md') || lower.endsWith('.markdown');
}
export function validateMdAttachment(input) {
    if (!isMarkdownFileName(input.fileName)) {
        return {
            ok: false,
            error: 'Приложите файл Markdown (.md или .markdown).',
        };
    }
    if (input.byteLength > MD_ATTACHMENT_MAX_BYTES) {
        return {
            ok: false,
            error: 'Файл слишком большой. Максимум 200 КБ.',
        };
    }
    return {
        ok: true,
        attachment: {
            fileName: input.fileName.trim(),
            content: input.content,
        },
    };
}
function isChatRole(value) {
    return value === 'user' || value === 'assistant';
}
function isMdAttachment(value) {
    if (value == null || typeof value !== 'object')
        return false;
    const item = value;
    return typeof item.fileName === 'string' && typeof item.content === 'string';
}
function isChatAction(value) {
    if (value == null || typeof value !== 'object')
        return false;
    const item = value;
    if (typeof item.id !== 'string' || typeof item.label !== 'string')
        return false;
    if (item.command !== 'RecordContact' &&
        item.command !== 'DeferPlacement' &&
        item.command !== 'MovePlacement' &&
        item.command !== 'AdvanceEngagement') {
        return false;
    }
    if (item.payload != null && typeof item.payload !== 'object')
        return false;
    return true;
}
function isChatMessage(value) {
    if (value == null || typeof value !== 'object')
        return false;
    const item = value;
    if (typeof item.id !== 'string')
        return false;
    if (!isChatRole(item.role))
        return false;
    if (typeof item.content !== 'string')
        return false;
    if (typeof item.createdAt !== 'string')
        return false;
    if (item.attachment !== undefined && !isMdAttachment(item.attachment)) {
        return false;
    }
    if (item.source !== undefined &&
        item.source !== 'user_turn' &&
        item.source !== 'proactive') {
        return false;
    }
    if (item.proactiveId !== undefined && typeof item.proactiveId !== 'string') {
        return false;
    }
    if (item.actions !== undefined) {
        if (!Array.isArray(item.actions) || !item.actions.every(isChatAction)) {
            return false;
        }
    }
    return true;
}
function isChatTab(value) {
    if (value == null || typeof value !== 'object')
        return false;
    const item = value;
    if (typeof item.id !== 'string')
        return false;
    if (typeof item.title !== 'string')
        return false;
    if (typeof item.createdAt !== 'string')
        return false;
    if (!Array.isArray(item.messages) || !item.messages.every(isChatMessage)) {
        return false;
    }
    return true;
}
export function parseChatStore(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const data = raw;
    if (!Array.isArray(data.tabs) || data.tabs.length === 0)
        return null;
    if (!data.tabs.every(isChatTab))
        return null;
    if (typeof data.activeTabId !== 'string')
        return null;
    const tabs = data.tabs;
    const activeExists = tabs.some((tab) => tab.id === data.activeTabId);
    if (!activeExists)
        return null;
    return {
        tabs,
        activeTabId: data.activeTabId,
    };
}
export function loadChatStore(storage = typeof localStorage !==
    'undefined'
    ? localStorage
    : null) {
    if (!storage)
        return createInitialStore();
    try {
        const raw = storage.getItem(AGENT_CHATS_STORAGE_KEY);
        if (!raw)
            return createInitialStore();
        const parsed = parseChatStore(JSON.parse(raw));
        if (!parsed)
            return createInitialStore();
        const scrubbed = scrubStubChatStore(parsed);
        if (scrubbed !== parsed) {
            saveChatStore(scrubbed, storage);
        }
        return scrubbed;
    }
    catch {
        return createInitialStore();
    }
}
export function saveChatStore(store, storage = typeof localStorage !==
    'undefined'
    ? localStorage
    : null) {
    if (!storage)
        return;
    try {
        storage.setItem(AGENT_CHATS_STORAGE_KEY, JSON.stringify(store));
    }
    catch {
        /* ignore quota */
    }
}
export function clampChatWidth(width) {
    if (!Number.isFinite(width))
        return AGENT_CHAT_WIDTH_DEFAULT;
    return Math.min(AGENT_CHAT_WIDTH_MAX, Math.max(AGENT_CHAT_WIDTH_MIN, Math.round(width)));
}
export function loadChatWidth(storage = typeof localStorage !==
    'undefined'
    ? localStorage
    : null) {
    if (!storage)
        return AGENT_CHAT_WIDTH_DEFAULT;
    try {
        const raw = storage.getItem(AGENT_CHAT_WIDTH_STORAGE_KEY);
        if (raw == null)
            return AGENT_CHAT_WIDTH_DEFAULT;
        return clampChatWidth(Number(raw));
    }
    catch {
        return AGENT_CHAT_WIDTH_DEFAULT;
    }
}
export function saveChatWidth(width, storage = typeof localStorage !==
    'undefined'
    ? localStorage
    : null) {
    if (!storage)
        return;
    try {
        storage.setItem(AGENT_CHAT_WIDTH_STORAGE_KEY, String(clampChatWidth(width)));
    }
    catch {
        /* ignore quota */
    }
}
export function getActiveTab(store) {
    const found = store.tabs.find((tab) => tab.id === store.activeTabId);
    return found ?? store.tabs[0];
}
export function addChatTab(store, now = new Date()) {
    const tab = createEmptyTab(now);
    return {
        tabs: [...store.tabs, tab],
        activeTabId: tab.id,
    };
}
export function selectChatTab(store, tabId) {
    if (!store.tabs.some((tab) => tab.id === tabId))
        return store;
    return { ...store, activeTabId: tabId };
}
export function closeChatTab(store, tabId) {
    if (store.tabs.length === 1) {
        const only = store.tabs[0];
        if (only.id !== tabId)
            return store;
        const cleared = {
            ...only,
            title: 'Новый чат',
            messages: [],
        };
        return { tabs: [cleared], activeTabId: cleared.id };
    }
    const tabs = store.tabs.filter((tab) => tab.id !== tabId);
    if (tabs.length === store.tabs.length)
        return store;
    const activeTabId = store.activeTabId === tabId ? tabs[tabs.length - 1].id : store.activeTabId;
    return { tabs, activeTabId };
}
export function sendUserMessage(store, input) {
    const content = input.content.trim();
    if (!content && !input.attachment)
        return store;
    const now = input.now ?? new Date();
    const iso = now.toISOString();
    const userMessage = {
        id: createId('msg'),
        role: 'user',
        content,
        createdAt: iso,
        attachment: input.attachment,
    };
    const assistantMessage = {
        id: createId('msg'),
        role: 'assistant',
        content: input.assistantContent !== undefined
            ? input.assistantContent
            : PENDING_AGENT_REPLY,
        createdAt: iso,
    };
    return {
        ...store,
        tabs: store.tabs.map((tab) => {
            if (tab.id !== store.activeTabId)
                return tab;
            const isFirstUser = tab.messages.every((m) => m.role !== 'user');
            return {
                ...tab,
                title: isFirstUser
                    ? titleFromUserMessage(content || input.attachment?.fileName || '')
                    : tab.title,
                messages: [...tab.messages, userMessage, assistantMessage],
            };
        }),
    };
}
export function setAssistantMessageContent(store, input) {
    return {
        ...store,
        tabs: store.tabs.map((tab) => ({
            ...tab,
            messages: tab.messages.map((message) => message.id === input.messageId
                ? { ...message, content: input.content }
                : message),
        })),
    };
}
export function appendProactiveAssistantMessage(store, input) {
    const already = store.tabs.some((tab) => tab.messages.some((message) => message.proactiveId === input.proactiveId));
    if (already)
        return store;
    const iso = (input.now ?? new Date()).toISOString();
    const actionsState = {};
    for (const action of input.actions ?? []) {
        actionsState[action.id] = 'idle';
    }
    const message = {
        id: createId('msg'),
        role: 'assistant',
        content: input.content,
        createdAt: iso,
        source: 'proactive',
        proactiveId: input.proactiveId,
        actions: input.actions,
        actionsState,
        actionError: null,
    };
    return {
        ...store,
        tabs: store.tabs.map((tab) => {
            if (tab.id !== store.activeTabId)
                return tab;
            return {
                ...tab,
                messages: [...tab.messages, message],
            };
        }),
    };
}
export function setMessageActionState(store, input) {
    return {
        ...store,
        tabs: store.tabs.map((tab) => ({
            ...tab,
            messages: tab.messages.map((message) => {
                if (message.id !== input.messageId)
                    return message;
                return {
                    ...message,
                    actionsState: {
                        ...(message.actionsState ?? {}),
                        [input.actionId]: input.state,
                    },
                    actionError: input.error !== undefined ? input.error : message.actionError,
                };
            }),
        })),
    };
}
